/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import React from 'react';
import { connect, useDispatch } from 'react-redux';
import get from 'lodash/get';
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';

import { paginator } from 'shared-ui/utils/paginator';
import { Select, FlexRow, FlexCol, Heading, announce, Alert, Button } from 'sarsaparilla';
import { Footer } from 'site-kit';

import Card from '../components/SearchResultCard/Card';
import { CardSurfaceContext } from '../components/SearchResultCard/CardSurfaceContext';
import CardLoading from '../components/SearchResultCard/CardLoading';
import * as searchActions from '../actions/search';
import * as searchMapSyncActions from '../actions/searchMapSync';
import SearchPagination from '../components/SearchPagination';
import SearchPaginationText from '../components/SearchPaginationText';
import SearchSpellingSuggestions from '../components/SearchSpellingSuggestions';
import SearchFilters from './SearchFilters';
import SearchCriteriaInputs from './SearchCriteriaInputs';
import SearchFilterPills from '../components/SearchFilterPills';
import AiFeedbackModal from '../components/aiSearch/AISearchModal';
import * as types from '../constants';
import AiSearchInput from '../components/aiSearch/AiSearchInput';
import { useMapControl } from '../context/MapControlContext';
import { setVisibleMapItems } from '../actions/searchMapSync';

import { A250SearchPromoWrapper } from '../components/America250Promo/A250Promo';
import { NoSearchResultsMessage } from '../components/NoSearchResultsMessage';

class SearchResultsList extends React.Component {
    static propTypes = {
        clickedMapItemId: PropTypes.string,
        screenWidth: PropTypes.number,
        dispatch: PropTypes.func.isRequired,
        search: PropTypes.object,
        searchResults: PropTypes.object,
        isCampingSearch: PropTypes.bool,
        hasDatesInfoModal: PropTypes.func,
        onShowAvailableModalChange: PropTypes.func,
        flags: PropTypes.object,
        searchAggregations: PropTypes.any,
        showAiSearch: PropTypes.bool,
        visibleMapItemIds: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.string),
            PropTypes.oneOf([null]),
        ]),
        fitBoundsToMarkers: PropTypes.func,
        resetVisibleMapItems: PropTypes.func,
    };

    static sortOptionsDefault = [
        {
            label: 'Best Match',
            value: 'score',
        },
        {
            label: 'Distance',
            value: 'distance',
        },
        {
            label: 'Rating',
            value: 'average_rating',
        },
        {
            label: 'Price',
            value: 'price asc',
        },
        {
            label: 'Mobile Coverage',
            value: 'aggregate_cell_coverage',
        },
    ];

    static sortOptionsAvailability = [
        {
            label: 'Best Match',
            value: 'score',
        },
        {
            label: 'Available',
            value: 'available',
        },
        {
            label: 'Distance',
            value: 'distance',
        },
        {
            label: 'Rating',
            value: 'average_rating',
        },
        {
            label: 'Price',
            value: 'price asc',
        },
        {
            label: 'Mobile Coverage',
            value: 'aggregate_cell_coverage',
        },
    ];

    static displayName = 'SearchResultsList';

    constructor(props) {
        super(props);

        this.handleSortChange = this.handleSortChange.bind(this);
        this.onCardLeaveEvent = this.onCardLeaveEvent.bind(this);
        this.onCardOverEvent = this.onCardOverEvent.bind(this);
        this.onPage = this.onPage.bind(this);
        this.onFilteredPage = this.onFilteredPage.bind(this);
        this.onSpellingSearch = this.onSpellingSearch.bind(this);
        this.onApplyFilters = this.onApplyFilters.bind(this);

        this.state = {
            isSurveyOpen: false,
            isAvailabilityAlertVisible: true,
            filteredStart: 0,
        };

        this.cardListDivRef = React.createRef(null);
    }

    componentDidUpdate(prevProps) {
        const { search, searchResults, visibleMapItemIds } = this.props;
        const {
            search: prevSearch,
            searchResults: prevSearchResults,
            visibleMapItemIds: prevVisibleMapItemIds,
        } = prevProps;

        const isShowOnlyAvailableSelected = this.isShowOnlyAvailableSelected(search);
        const prevIsShowOnlyAvailableSelected =
            this.isShowOnlyAvailableSelected(prevSearch);

        if (
            ((!prevIsShowOnlyAvailableSelected && isShowOnlyAvailableSelected) ||
                (searchResults?.total === 0 && prevSearchResults?.total !== 0)) &&
            !this.state.isAvailabilityAlertVisible
        ) {
            this.onAvailabilityAlertChange(true);
        }

        // Reset filtered pagination when visible map items change
        if (visibleMapItemIds !== prevVisibleMapItemIds) {
            this.setState({ filteredStart: 0 });
        }
    }

    isShowOnlyAvailableSelected(search) {
        return (
            !search?.include_unavailable &&
            !search?.include_notreservable &&
            !search?.include_partially_available
        );
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.clickedMapItemId &&
            nextProps.clickedMapItemId !== this.props.clickedMapItemId
        ) {
            const searchResults = this.props.searchResults;
            if (!searchResults?.results?.length) return;
            let card;
            let entityType;
            let inPage;

            for (let i = 0; i < searchResults.results.length; i += 1) {
                const searchResult = searchResults.results[i];

                // TODO: Should we also check for entity_type???
                if (searchResult.entity_id === nextProps.clickedMapItemId) {
                    let pageSize = this.props.search.size;

                    if (!pageSize) {
                        pageSize = 20;
                    }

                    entityType = searchResult.entity_type;
                    inPage = Math.ceil((i + 1) / pageSize);
                    break;
                }
            }

            card = document.getElementById(
                `rec-card-${nextProps.clickedMapItemId}_${entityType}`
            );

            if (card) {
                card.scrollIntoView({
                    behavior: 'smooth',
                });
                setTimeout(() => {
                    card.classList.toggle('rec-list-item-highlight');
                }, 500);
                setTimeout(() => {
                    card.classList.toggle('rec-list-item-highlight');
                }, 1000);
            } else {
                this.onPage(inPage);

                const interval = window.setInterval(() => {
                    card = document.getElementById(
                        `rec-card-${nextProps.clickedMapItemId}_${entityType}`
                    );

                    if (card) {
                        window.clearInterval(interval);
                        card.scrollIntoView({
                            behavior: 'smooth',
                        });
                        setTimeout(() => {
                            card.classList.toggle('rec-list-item-highlight');
                        }, 500);
                        setTimeout(() => {
                            card.classList.toggle('rec-list-item-highlight');
                        }, 1000);
                    }
                }, 100);
            }
        }

        if (
            nextProps.search.entity_id &&
            (!this.props.search.start || this.props.search.start === 0) &&
            this.props.searchResults?.exactResult?.entity_id !==
                nextProps.search.entity_id &&
            nextProps.searchResults?.exactResult?.entity_id !== nextProps.search.entity_id
        ) {
            const searchResults = this.props.searchResults;
            if (
                searchResults?.results?.length > 0 &&
                searchResults?.results[0].entity_id !== this.props.search.entity_id
            ) {
                this.props.dispatch(
                    searchActions.fetchExactItem(
                        this.props.search.entity_id,
                        this.props.search.entity_type,
                        this.props.search.checkin_time,
                        this.props.search.checkout_time,
                        this.props.search
                    )
                );
            }
        }
    }

    onCardLeaveEvent() {
        this.props.dispatch(searchMapSyncActions.handleDeselectCard());
    }

    onCardOverEvent(item) {
        this.props.dispatch(searchMapSyncActions.handleSelectCard(item));
    }

    onApplyFilters = () => {
        this.onPage(1);
        setTimeout(() => {
            this.props.dispatch(searchActions.navigateToSearchResults());
        }, 200);
    };

    onPage(page) {
        let pageSize = this.props.search.size;

        if (!pageSize) {
            pageSize = 20;
        }

        const newStart = (page - 1) * pageSize;

        this.props.dispatch(
            searchActions.updateSearchCriterias({
                start: newStart,
            })
        );

        // Update the 'start' URL search param
        const url = new URL(window.location.href);
        url.searchParams.set('start', newStart);
        window.history.replaceState({}, '', url.toString());

        // Reset scroll position
        window.scrollTo(0, 0);
        if (this.cardListDivRef.current) {
            // Reset scroll position of the card list div
            this.cardListDivRef.current.scrollTo(0, 0);
        }
    }

    onFilteredPage(page) {
        let pageSize = this.props.search.size;

        if (!pageSize) {
            pageSize = 20;
        }

        const newStart = (page - 1) * pageSize;
        this.setState({ filteredStart: newStart });

        // Reset scroll position
        window.scrollTo(0, 0);
        if (this.cardListDivRef.current) {
            this.cardListDivRef.current.scrollTo(0, 0);
        }
    }

    onSpellingSearch(searchText, exact, event) {
        event.preventDefault();
        this.props.dispatch(
            searchActions.updateSearchCriterias({
                what: searchText,
                exact,
            })
        );
        setTimeout(() => {
            this.props.dispatch(searchActions.fetchSearchResults());
        }, 200);
    }

    getCampsiteTypeFilterValues() {
        const campsiteTypes = get(this.props.search, 'uiFilters.campsiteTypes', {});
        const hasElectricity = !isEmpty(
            get(
                this.props.search,
                'uiFilters.campsiteAmenities.campsite-amenities-electricity-hookup',
                {}
            )
        );
        let siteTypes = [];
        forEach(campsiteTypes, (campsiteTypeObject) => {
            const campsiteFilters = get(campsiteTypeObject, 'filters', []);
            forEach(campsiteFilters, (f) => {
                let filters = [];
                if (f.filterValue && f.filterValue !== '') {
                    filters = f.filterValue.split(',');
                }
                if (!isEmpty(filters)) {
                    siteTypes.push(...filters);
                }
            });
        });

        if (hasElectricity) {
            siteTypes = filter(siteTypes, (s) => {
                return !s.includes('NONELECTRIC');
            });
        }
        return siteTypes;
    }

    handleSortChange(value) {
        let announceSelection = null;
        switch (value) {
            case 'available':
                announceSelection = 'availability';
                break;
            case 'distance':
                announceSelection = 'distance';
                break;
            case 'price asc':
                announceSelection = 'price low to high';
                break;
            case 'price desc':
                announceSelection = 'price high to low';
                break;
            case 'score':
            default:
                announceSelection = 'best match';
                break;
        }
        if (value === 'distance') {
            this.props.dispatch(
                searchActions.updateSearchCriterias({
                    start: null,
                    cursor_mark: null,
                    sort: value,
                    lat: this.props.search.map_center_lat,
                    lng: this.props.search.map_center_lng,
                })
            );
            announce(`The search results are now sorted by ${announceSelection}.`);
        } else {
            this.props.dispatch(
                searchActions.updateSearchCriterias({
                    start: null,
                    cursor_mark: null,
                    sort: value,
                })
            );
            announce(`The search results are now sorted by ${announceSelection}.`);
        }
        setTimeout(() => {
            this.props.dispatch(searchActions.navigateToSearchResults());
        }, 200);
    }

    onResetMapClick = () => {
        if (this.props.fitBoundsToMarkers) {
            this.props.fitBoundsToMarkers({
                speed: 3,
                maxDuration: 3000,
            });
            this.props.resetVisibleMapItems(null);
        }
    };

    isInventoryFiltersApplied = () => {
        const uiFilters = this.props.search.uiFilters || {};
        let inventoryFiltersApplied = false;
        for (const propName in uiFilters) {
            if (isObject(uiFilters[propName]) && !isEmpty(uiFilters[propName])) {
                for (const key in uiFilters[propName]) {
                    if (key in uiFilters[propName]) {
                        const uiFilter = uiFilters[propName][key];
                        if (
                            propName !== 'inventoryTypes' &&
                            uiFilter?.filters?.length > 0
                        ) {
                            inventoryFiltersApplied = true;
                            break;
                        }
                    }
                }
            }
        }
        return inventoryFiltersApplied;
    };

    renderExactMatchResult = () => {
        const item = this.props.searchResults.exactResult;
        if (
            !item ||
            (this.props.search.start && this.props.search.start > 0) ||
            item.entity_type === 'recarea'
        ) {
            return null;
        }
        let highlightClass = '';
        const inventoryFiltersApplied = this.isInventoryFiltersApplied();
        const siteTypes = this.getCampsiteTypeFilterValues();

        let alert = null;

        if (
            inventoryFiltersApplied &&
            this.props.search?.entity_id === item.entity_id &&
            this.props.search.what?.toLowerCase() === item.name?.toLowerCase()
        ) {
            alert = (
                <Alert
                    className={'mb-2'}
                    type="info"
                    heading="We found a matching result, but it is not available for your filters"
                >
                    Try updating your filters or exploring a similar option below that
                    matches your criteria
                </Alert>
            );
            highlightClass += ' search-result-highlight--info';
        }

        return (
            <FlexCol xs={12}>
                {alert}
                <CardSurfaceContext.Provider value="list">
                    <Card
                        checkinTime={this.props.search.checkin_time}
                        checkoutTime={this.props.search.checkout_time}
                        dynamicClass={highlightClass}
                        item={item}
                        layout="campground"
                        onCardOver={this.onCardOverEvent}
                        onCardLeave={this.onCardLeaveEvent}
                        siteTypes={item.entity_type === 'campground' ? siteTypes : []}
                        val={highlightClass}
                        screenWidth={this.props.screenWidth}
                    />
                </CardSurfaceContext.Provider>
            </FlexCol>
        );
    };

    onOpenSurvey = () => {
        this.setState({
            isSurveyOpen: true,
        });
    };

    onCloseSurvey = () => {
        this.setState({
            isSurveyOpen: false,
        });
    };

    onAvailabilityAlertChange = (value) => {
        this.setState({
            isAvailabilityAlertVisible: value,
        });
    };

    renderError() {
        const { err } = this.props.searchResults;
        if (err) {
            let msg = `Oops! We're having technical difficulties.
                    This is a temporary issue on our servers. Please try your search again.`;
            if (err.includes('Too Many Requests')) {
                msg =
                    'We are currently experiencing heavy traffic on our site. Please try again.';
            }
            return (
                <FlexCol xs={12}>
                    <Alert className={'mb-1'} type="error">
                        {msg}{' '}
                    </Alert>
                </FlexCol>
            );
        }
        return null;
    }

    renderSearchResults() {
        const siteTypes = this.getCampsiteTypeFilterValues();
        const isLoading = this.props.searchResults.loading;
        const exactItem = this.renderExactMatchResult();
        const searchSize = this.props.search.size || 20;
        const allResults = this.props.searchResults.results || [];

        // Filter results by visible map items BEFORE pagination
        const hasHiddenResults = Array.isArray(this.props.visibleMapItemIds);
        const filteredResults = hasHiddenResults
            ? allResults.filter((item) =>
                  this.props.visibleMapItemIds.includes(item.entity_id)
              )
            : allResults;

        // Use filtered pagination state when filtering, otherwise use Redux state
        const currentStart = hasHiddenResults
            ? this.state.filteredStart
            : this.props.search.start || 0;

        const { paginatedData } = paginator({
            items: filteredResults,
            activeIndex: currentStart / searchSize,
            perPage: searchSize,
        });

        const hasActivityTagsApplied =
            Object.keys(this.props.search.uiFilters?.activityTags || {}).length > 0;

        // added this just to test out the new alerts
        return (
            <FlexRow className="search-outer-wrap">
                {hasActivityTagsApplied && (
                    <FlexCol xs={12}>
                        <Alert className="mb-2" type="warning" hasAriaLive>
                            {this.props.screenWidth >= 992
                                ? 'Please confirm desired activity is available at location before booking.'
                                : 'Please confirm desired activity is available at location before booking.'}
                        </Alert>
                    </FlexCol>
                )}
                {this.renderError()}
                {exactItem}
                {paginatedData.map((item, index) => {
                    if (
                        exactItem &&
                        this.props.searchResults?.exactResult?.entity_id ===
                            item.entity_id &&
                        this.props.searchResults?.exactResult?.entity_type ===
                            item.entity_type
                    ) {
                        return null;
                    }

                    let highlightClass = '';

                    if (this.props.search.highlighted_item === item.entity_id) {
                        highlightClass = 'rec-list-item-highlight';
                    }

                    if (isLoading) {
                        return <CardLoading key={index} />;
                    }
                    let alert = null;
                    if (
                        !exactItem &&
                        index === 0 &&
                        (this.props.search.entity_id === item.entity_id ||
                            this.props.search.what?.toLowerCase() ===
                                item.name?.toLowerCase())
                    ) {
                        if (
                            this.props.search.checkin_time &&
                            this.props.search.checkout_time &&
                            item.availability === 'unavailable'
                        ) {
                            // if first matching result is unavailable
                            alert = (
                                <Alert
                                    className={'mb-2'}
                                    type="info"
                                    heading="We found a matching result, but there is currently no availability"
                                >
                                    Try updating your dates or explore a similar option
                                    below
                                </Alert>
                            );
                            highlightClass += ' search-result-highlight--info';
                        } else {
                            // if first result is available or is matching
                            let msg = 'We found these matching results for your search';
                            if (item.entity_type === 'recarea') {
                                msg =
                                    'We found a matching result with your search. Try exploring some of the below nearby campgrounds that are available for your dates and filters';
                            }
                            alert = (
                                <Alert className={'mb-2'} type="success">
                                    {msg}
                                </Alert>
                            );
                            highlightClass += ' search-result-highlight--success';
                        }
                    }

                    return (
                        <FlexCol xs={12} key={`card_${item.id}`}>
                            {alert}
                            <CardSurfaceContext.Provider value="list">
                                <Card
                                    checkinTime={this.props.search.checkin_time}
                                    checkoutTime={this.props.search.checkout_time}
                                    dynamicClass={highlightClass}
                                    item={item}
                                    layout="campground"
                                    onCardOver={this.onCardOverEvent}
                                    onCardLeave={this.onCardLeaveEvent}
                                    siteTypes={
                                        item.entity_type === 'campground' ? siteTypes : []
                                    }
                                    val={highlightClass}
                                    screenWidth={this.props.screenWidth}
                                    isCampingSearch={this.props.isCampingSearch}
                                />
                            </CardSurfaceContext.Provider>
                        </FlexCol>
                    );
                })}
            </FlexRow>
        );
    }

    render() {
        let sortOptions;

        if (
            this.props.isCampingSearch ||
            (this.props.search.checkin_time && this.props.search.checkout_time)
        ) {
            sortOptions = SearchResultsList.sortOptionsAvailability;
        } else {
            sortOptions = SearchResultsList.sortOptionsDefault;
        }

        let currentSortSelection = this.props.search.sort;

        if (!currentSortSelection) {
            currentSortSelection = 'score';
        }

        const allResults = this.props.searchResults.results || [];
        const hasHiddenResults = Array.isArray(this.props.visibleMapItemIds);

        // Calculate total based on whether we're filtering by visible map items
        const filteredResults = hasHiddenResults
            ? allResults.filter((item) =>
                  this.props.visibleMapItemIds.includes(item.entity_id)
              )
            : allResults;
        const total = hasHiddenResults
            ? filteredResults.length
            : this.props.searchResults.total;
        const hiddenResultsCount = hasHiddenResults
            ? allResults.length - filteredResults.length
            : 0;

        // Use filtered pagination state when filtering, otherwise use Redux state
        const currentStart = hasHiddenResults
            ? this.state.filteredStart
            : this.props.search.start || 0;

        let searchResultsText = 'Search Results';
        if (this.props.searchResults.isBrowserLocation) {
            searchResultsText = 'Search Results Nearby';
        } else if (this.props.searchResults.placename) {
            searchResultsText = `Search Results near ${this.props.searchResults.placename}`;
        }

        return (
            <div
                ref={this.cardListDivRef}
                role="region"
                aria-label="Search Results List"
                className={cx('search-left-rail-wrap', {
                    'ai-search':
                        this.props.showAiSearch && !this.props.search?.filtersVisible,
                })}
            >
                <div
                    className={`${
                        this.props.search.filtersVisible ? 'search-filters-visible' : ''
                    }`}
                >
                    {this.props.screenWidth < 768 && (
                        <>
                            <div className="header-filters-container header-primary">
                                <SearchCriteriaInputs
                                    page={types.MAIN_SEARCH_RESULTS_PAGE}
                                    onApplyFilters={this.onApplyFilters}
                                    isCampingSearch={this.props.isCampingSearch}
                                    screenWidth={this.props.screenWidth}
                                    flags={this.props.flags}
                                    hasDatesInfoModal={this.props.hasDatesInfoModal}
                                    onShowAvailableModalChange={
                                        this.props.onShowAvailableModalChange
                                    }
                                />
                            </div>
                            {this.props.showAiSearch && (
                                <AiSearchInput
                                    isMobile
                                    onApplyFilters={this.onApplyFilters}
                                />
                            )}
                        </>
                    )}

                    {this.props.search.filtersVisible && (
                        <SearchFilters
                            onApplyFilters={this.onApplyFilters}
                            hasDatesInfoModal={this.props.hasDatesInfoModal}
                            searchAggregations={this.props.searchAggregations}
                            onShowAvailableModalChange={
                                this.props.onShowAvailableModalChange
                            }
                            screenWidth={this.props.screenWidth}
                        />
                    )}

                    {!this.props.search.filtersVisible && (
                        <>
                            <div
                                className="search-left-rail-results"
                                id="search-results-list"
                            >
                                <FlexRow>
                                    <FlexCol>
                                        <SearchSpellingSuggestions
                                            search={this.props.search}
                                            searchResults={this.props.searchResults}
                                            onSpellingSearch={this.onSpellingSearch}
                                        />
                                    </FlexCol>
                                </FlexRow>

                                <FlexRow justifyContent="space-between">
                                    <FlexCol xs={12} sm={9}>
                                        <Heading headingLevel={1} appearance="h6">
                                            {decodeURI(searchResultsText || '')}
                                        </Heading>
                                    </FlexCol>
                                    {this.props.showAiSearch &&
                                        !this.props.flags.enableAiSearchInput && (
                                            <FlexCol sm="variable" className="pb-2">
                                                <Button
                                                    appearance="link"
                                                    size="sm"
                                                    clickTagCategory="Search"
                                                    clickTagAction="AI Search Feedback Button"
                                                    clickTagLabel="Search Results"
                                                    aria-label={`Click to provide feedback to AI Search`}
                                                    onClick={this.onOpenSurvey}
                                                >
                                                    Provide Feedback on AI Search
                                                </Button>
                                            </FlexCol>
                                        )}
                                </FlexRow>
                                <FlexRow>
                                    <FlexCol>
                                        <SearchFilterPills flags={this.props.flags} />
                                    </FlexCol>
                                </FlexRow>
                                {/* {(total === 0 || isShowOnlyAvailableSelected) &&
                                    this.state.isAvailabilityAlertVisible && (
                                        <FlexRow className="mb-2">
                                            <FlexCol>
                                                <Alert
                                                    onCloseButtonClick={() =>
                                                        this.onAvailabilityAlertChange(
                                                            false
                                                        )
                                                    }
                                                    type="info"
                                                    showCloseButton
                                                >
                                                    Some results may be hidden.{' '}
                                                    <Button
                                                        onClick={this.onResetFilters}
                                                        appearance="link"
                                                        className="search-availability-alert"
                                                    >
                                                        Click here to see all search
                                                        results.
                                                    </Button>
                                                </Alert>
                                            </FlexCol>
                                        </FlexRow>
                                    )} */}
                                {total === 0 && !this.props.searchResults.loading ? (
                                    <NoSearchResultsMessage
                                        hasHiddenResults={hasHiddenResults}
                                        hiddenResultsCount={hiddenResultsCount}
                                        isResetMapDisabled={
                                            !this.props.fitBoundsToMarkers
                                        }
                                        onResetMapClick={this.onResetMapClick}
                                    />
                                ) : null}

                                <FlexRow
                                    hasGutters={false}
                                    className="search-total-filters"
                                >
                                    <FlexCol
                                        xs={5}
                                        xl={8}
                                        className="search-pagination-hide"
                                    >
                                        <SearchPaginationText
                                            start={currentStart}
                                            pageSize={this.props.search.size}
                                            total={total}
                                            isLoading={this.props.searchResults.loading}
                                            maxResults={searchActions.MAX_SEARCH_RESULTS}
                                        >
                                            {hasHiddenResults &&
                                                hiddenResultsCount > 0 && (
                                                    <span className="color-text-03">
                                                        {hiddenResultsCount} additional
                                                        {hiddenResultsCount === 1
                                                            ? ' result'
                                                            : ' results'}{' '}
                                                        off the map.{' '}
                                                        <button
                                                            type="button"
                                                            className="search--reset-map-zoom-button text-sm color-text-02"
                                                            onClick={this.onResetMapClick}
                                                            disabled={
                                                                !this.props
                                                                    .fitBoundsToMarkers
                                                            }
                                                        >
                                                            Recenter Map
                                                        </button>
                                                    </span>
                                                )}
                                        </SearchPaginationText>
                                    </FlexCol>

                                    {total > 0 && (
                                        <FlexCol
                                            xs={12}
                                            sm={7}
                                            xl={4}
                                            className={'search-sort-dropdown'}
                                        >
                                            <Select
                                                id={'search-sort-dropdown'}
                                                label="Sort By"
                                                isLabelVisible
                                                isInline
                                                options={sortOptions}
                                                onChange={(e) =>
                                                    this.handleSortChange(e.target.value)
                                                }
                                                value={currentSortSelection}
                                                gaTrackingId="189307795743"
                                            />
                                        </FlexCol>
                                    )}
                                </FlexRow>
                                {/* FlexRow for the search results defined above */}
                                {this.renderSearchResults()}

                                <A250SearchPromoWrapper />

                                <SearchPagination
                                    start={currentStart}
                                    pageSize={this.props.search.size}
                                    total={total}
                                    maxResults={searchActions.MAX_SEARCH_RESULTS}
                                    onPage={
                                        hasHiddenResults
                                            ? this.onFilteredPage
                                            : this.onPage
                                    }
                                />
                            </div>

                            <div className="results-footer-wrap">
                                <Footer inline />
                            </div>
                        </>
                    )}
                </div>
                <AiFeedbackModal
                    isSurveyOpen={this.state.isSurveyOpen}
                    onCloseSurvey={this.onCloseSurvey}
                />
            </div>
        );
    }
}

function select(state) {
    return {
        search: state.search,
        searchResults: state.searchResults,
        searchAggregations: state.searchAggregations,
        visibleMapItemIds: state.searchMapSync?.visibleMapItemIds ?? null,
    };
}

const ConnectedSearchResultsList = connect(select)(SearchResultsList);

function SearchResultsListWithMapControl({
    clickedMapItemId,
    screenWidth,
    isCampingSearch,
    hasDatesInfoModal,
    onShowAvailableModalChange,
    flags,
    showAiSearch,
}) {
    const { fitBoundsToMarkers } = useMapControl();
    const dispatch = useDispatch();
    return (
        <ConnectedSearchResultsList
            clickedMapItemId={clickedMapItemId}
            screenWidth={screenWidth}
            isCampingSearch={isCampingSearch}
            hasDatesInfoModal={hasDatesInfoModal}
            onShowAvailableModalChange={onShowAvailableModalChange}
            flags={flags}
            showAiSearch={showAiSearch}
            fitBoundsToMarkers={fitBoundsToMarkers}
            resetVisibleMapItems={() => {
                dispatch(setVisibleMapItems(null));
            }}
        />
    );
}

SearchResultsListWithMapControl.propTypes = {
    clickedMapItemId: PropTypes.string,
    screenWidth: PropTypes.number,
    isCampingSearch: PropTypes.bool,
    hasDatesInfoModal: PropTypes.func,
    onShowAvailableModalChange: PropTypes.func,
    flags: PropTypes.object,
    showAiSearch: PropTypes.bool,
};

export default SearchResultsListWithMapControl;

// cSpell:ignore criterias, recarea, checkin, notreservable
